import { useState } from 'react';
import type { PublicStoreConfig, StoreProduct } from '../types';

interface Props {
  config: PublicStoreConfig;
  storeName: string;
  storeInitial: string;
  featuredProducts: StoreProduct[];
  onStartChat: () => void;
}

function ProductImage({ product }: { product: StoreProduct }) {
  const [failed, setFailed] = useState(false);

  if (!product.imageUrl || failed) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-white/10 text-white/40">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
          <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
          <path d="M3 6h18M16 10a4 4 0 0 1-8 0" />
        </svg>
      </div>
    );
  }

  return (
    <img
      src={product.imageUrl}
      alt={product.name}
      onError={() => setFailed(true)}
      className="h-full w-full object-cover"
    />
  );
}

export function HeroSection({
  config,
  storeName,
  storeInitial,
  featuredProducts,
  onStartChat,
}: Props) {
  const leadProduct = featuredProducts[0];
  const supportingProducts = featuredProducts.slice(1, 3);

  return (
    <section
      className="relative overflow-hidden px-4 pb-14 pt-10 sm:pb-20 sm:pt-16"
      style={{ backgroundColor: 'var(--brand-primary)' }}
    >
      <div className="storefront-grid pointer-events-none absolute inset-0 opacity-30" />
      <div
        className="pointer-events-none absolute -right-24 -top-32 h-96 w-96 rounded-full opacity-20 blur-3xl"
        style={{ backgroundColor: 'var(--brand-accent)' }}
      />

      <div className="relative z-10 mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-[minmax(0,1fr)_480px] lg:gap-16">
        <div className="max-w-2xl">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-medium text-white/80 backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
            Shopping assistant online
          </div>

          <h1 className="max-w-xl text-balance text-4xl font-bold leading-[1.04] tracking-[-0.045em] text-white sm:text-6xl">
            Find something you’ll love at {storeName}.
          </h1>

          <p className="mt-5 max-w-xl text-base leading-7 text-white/70 sm:text-lg">
            {config.storeDescription?.trim() ||
              'Browse the collection or ask our shopping assistant for a personal recommendation.'}
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <button
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full px-6 text-sm font-semibold text-white shadow-lg transition-all hover:-translate-y-0.5 hover:brightness-110 active:translate-y-0 active:scale-[0.98]"
              style={{ backgroundColor: 'var(--brand-accent)' }}
              onClick={onStartChat}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              Ask for a recommendation
            </button>
            <button
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-white/20 bg-white/10 px-6 text-sm font-semibold text-white transition-all hover:bg-white/15 active:scale-[0.98]"
              onClick={() => document.getElementById('store-products')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Browse products
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 5v14M19 12l-7 7-7-7" />
              </svg>
            </button>
          </div>

          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs font-medium text-white/55">
            <span>Personal recommendations</span>
            <span>Instant product answers</span>
            <span>Checkout in chat</span>
          </div>
        </div>

        <div className="relative mx-auto hidden h-[430px] w-full max-w-[480px] lg:block" aria-label="Featured products">
          {leadProduct ? (
            <div className="absolute inset-y-0 right-0 w-[72%] overflow-hidden rounded-[32px] border border-white/15 bg-white/10 shadow-2xl">
              <ProductImage product={leadProduct} />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent p-6 pt-20">
                <p className="text-sm font-semibold text-white">{leadProduct.name}</p>
                <p className="mt-1 text-xs text-white/65">Featured from the collection</p>
              </div>
            </div>
          ) : (
            <div className="absolute inset-y-0 right-0 flex w-[72%] items-center justify-center rounded-[32px] border border-white/15 bg-white/10 text-5xl font-bold text-white/25">
              {config.logoUrl ? (
                <img src={config.logoUrl} alt="" className="h-24 w-24 rounded-3xl object-cover" />
              ) : (
                storeInitial
              )}
            </div>
          )}

          {supportingProducts.map((product, index) => (
            <div
              key={product.id}
              className={`absolute left-0 h-40 w-40 overflow-hidden rounded-[24px] border-4 border-white/10 bg-white/10 shadow-xl ${
                index === 0 ? 'top-7 -rotate-3' : 'bottom-7 rotate-3'
              }`}
            >
              <ProductImage product={product} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default HeroSection;
